import type { RcFile } from 'antd/es/upload'

import {
  isSvgFile,
  SVG_ACCEPT,
  svgRejection,
  type SvgRejection
} from 'src/utils'

/**
 * Workspace-picture constraints, taken from the platform's company-logo field
 * (`apps/crm/src/features/settings/.../SettingsForm.tsx` → `validatePhoto` in
 * `apps/crm/src/utils/upload.ts`), plus SVG, which is vetted for scripts
 * instead of capped in pixels.
 *
 * The platform caps **pixel dimensions**, not bytes — there is no byte limit
 * anywhere in it, and the backend's `CreateUploadUrl` contract carries no
 * constraints at all.
 */
const RASTER_TYPES = ['image/png', 'image/jpeg']

export const LOGO_ACCEPT = [...RASTER_TYPES, SVG_ACCEPT].join(',')
export const LOGO_MAX_DIMENSION = 600

export type LogoRejection =
  | 'format'
  | 'dimensions'
  | 'unreadable'
  | SvgRejection

/**
 * Resolves when the file is a usable workspace picture, rejects with the reason
 * otherwise. Dimensions can only be read once the browser has decoded the
 * image, hence the promise — `beforeUpload` accepts one.
 */
export const validateLogo = (file: RcFile): Promise<void> =>
  new Promise((resolve, reject: (reason: LogoRejection) => void) => {
    const isSvg = isSvgFile(file)

    if (!isSvg && !RASTER_TYPES.includes(file.type)) {
      reject('format')

      return
    }

    const objectUrl = URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)

      if (isSvg) {
        svgRejection(file)
          .then((problem) => (problem ? reject(problem) : resolve()))
          .catch(() => reject('unreadable'))

        return
      }

      if (
        image.width > LOGO_MAX_DIMENSION ||
        image.height > LOGO_MAX_DIMENSION
      ) {
        reject('dimensions')

        return
      }

      resolve()
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject('unreadable')
    }

    image.src = objectUrl
  })
